import React, { useMemo } from 'react';
import { Progress } from '../ui/progress';
import { 
  Check, Clock, Users, Sparkles, Flag, MoreHorizontal, Phone,
  Coffee, Sun, Moon, Target, Video, AlertTriangle, TrendingUp, 
  CheckCircle2, MessageSquare, Calendar, Brain, FileText
} from 'lucide-react';
import { DESIGN } from '../DesignSystem';
import { COGNITIVE_LANES } from '../../mock';

// Icon mapping for dynamic rendering
const ICON_MAP = {
  Phone: Phone,
  Video: Video,
  AlertTriangle: AlertTriangle,
  TrendingUp: TrendingUp,
  Target: Target,
  CheckCircle2: CheckCircle2,
  MessageSquare: MessageSquare,
  Clock: Clock,
  Users: Users,
  Coffee: Coffee,
  Calendar: Calendar
};

// Lane Mismatch Indicator Component
const LaneMismatchIndicator = ({ taskLane, assigneeLane, assigneeName }) => {
  const taskLaneData = COGNITIVE_LANES.find(l => l.id === taskLane);
  const assigneeLaneData = COGNITIVE_LANES.find(l => l.id === assigneeLane);
  
  if (!taskLane || !assigneeLane || taskLane === assigneeLane) return null;
  
  return (
    <div className="relative group inline-flex">
      <div 
        className="flex items-center gap-1 px-2 py-1 rounded-md cursor-help"
        style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}
        data-testid="lane-mismatch-indicator"
      >
        <AlertTriangle className="w-3 h-3" style={{ color: '#F59E0B' }} />
        <span className="text-xs font-medium" style={{ color: '#F59E0B' }}>
          Lane Mismatch
        </span>
      </div>
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-0 mb-2 w-64 p-3 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 bg-white border" style={{ borderColor: DESIGN.colors.border }}>
        <p className="text-xs leading-relaxed" style={{ color: DESIGN.colors.textSecondary }}>
          This task is tagged <span className="font-semibold" style={{ color: taskLaneData?.color }}>{taskLaneData?.name}</span> but {assigneeName ? <span className="font-semibold">{assigneeName}</span> : 'the assignee'} is primarily a <span className="font-semibold" style={{ color: assigneeLaneData?.color }}>{assigneeLaneData?.name}</span> thinker. Consider reassigning or providing additional context.
        </p>
        <div className="flex items-center gap-2 mt-2 pt-2 border-t" style={{ borderColor: DESIGN.colors.border }}>
          <Brain className="w-3 h-3" style={{ color: '#00C9A7' }} />
          <span className="text-xs font-medium" style={{ color: '#00C9A7' }}>Find Cognitive Match</span>
        </div>
      </div>
    </div>
  );
};

// ================================================
// FOCUS ITEM CARD - Individual Focus Item
// ================================================
export const FocusItemCard = ({ item, onToggle, onAction, showMismatchIndicator = false, onGenerateBrief }) => {
  const IconComponent = ICON_MAP[item.icon] || Target;
  
  // Detect if this is a 1:1 meeting (meeting with exactly 1 other participant)
  const isOneOnOne = (item.type === 'meeting' || item.type === 'call') && 
    item.participants && 
    item.participants.length === 1;
  
  const priorityColors = {
    critical: { bg: DESIGN.colors.errorSubtle, text: DESIGN.colors.error, border: 'rgba(239, 68, 68, 0.3)' },
    high: { bg: DESIGN.colors.errorSubtle, text: DESIGN.colors.error, border: 'rgba(239, 68, 68, 0.3)' },
    medium: { bg: DESIGN.colors.amberSubtle, text: '#D97706', border: 'rgba(245, 158, 11, 0.3)' },
    low: { bg: DESIGN.colors.blueSubtle, text: '#4F46E5', border: 'rgba(79, 70, 229, 0.3)' }
  };
  const pStyle = priorityColors[item.priority] || priorityColors.medium;

  return (
    <div 
      className={`group rounded-2xl border transition-all duration-300 hover:shadow-lg ${
        item.completed ? 'opacity-60' : ''
      }`}
      style={{ 
        backgroundColor: DESIGN.colors.card,
        borderColor: item.completed ? DESIGN.colors.border : DESIGN.colors.borderSubtle,
        borderLeft: `4px solid ${item.completed ? DESIGN.colors.success : item.color || DESIGN.colors.primary}`
      }}
      data-testid={`focus-item-${item.id}`}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Completion checkbox */}
          <button
            onClick={() => onToggle(item.id)}
            className={`mt-0.5 w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
              item.completed 
                ? '' 
                : 'hover:border-green-400 group-hover:border-green-400'
            }`}
            style={{
              backgroundColor: item.completed ? DESIGN.colors.success : 'transparent',
              borderColor: item.completed ? DESIGN.colors.success : DESIGN.colors.border
            }}
            data-testid={`toggle-item-${item.id}`}
          >
            {item.completed && <Check className="w-4 h-4 text-white" />}
          </button>

          {/* Icon */}
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ 
              backgroundColor: `${item.color}15`,
              boxShadow: `0 0 15px ${item.color}20`
            }}
          >
            <IconComponent className="w-5 h-5" style={{ color: item.color }} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h4 
                className={`text-base font-bold ${item.completed ? 'line-through' : ''}`}
                style={{ color: item.completed ? DESIGN.colors.textMuted : DESIGN.colors.textPrimary }}
              >
                {item.title}
              </h4>
              <span 
                className="text-xs px-3 py-1 rounded-full flex-shrink-0 capitalize font-semibold"
                style={{ 
                  backgroundColor: pStyle.bg, 
                  color: pStyle.text,
                  boxShadow: `inset 0 0 0 1px ${pStyle.border}`
                }}
              >
                {item.priority}
              </span>
            </div>
            
            <p 
              className="text-sm mb-3 line-clamp-2"
              style={{ color: item.completed ? DESIGN.colors.textMuted : DESIGN.colors.textSecondary }}
            >
              {item.description}
            </p>

            {/* Meta row */}
            <div className="flex items-center gap-4 text-xs flex-wrap" style={{ color: DESIGN.colors.textMuted }}>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                <span className="font-medium">{item.time}</span>
              </div>
              <span className="opacity-50">•</span>
              <span className="font-medium">{item.duration}</span>
              {item.participants && item.participants.length > 0 && (
                <>
                  <span className="opacity-50">•</span>
                  <div className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    <span className="font-medium">{item.participants.length}</span>
                  </div>
                </>
              )}
              {item.source && (
                <>
                  <span className="opacity-50">•</span>
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: DESIGN.colors.primary }} />
                    <span className="font-medium">{item.source}</span>
                  </div>
                </>
              )}
              {/* Lane Mismatch Indicator */}
              {showMismatchIndicator && item.taskLane && item.assigneeLane && item.taskLane !== item.assigneeLane && (
                <>
                  <span className="opacity-50">•</span>
                  <LaneMismatchIndicator 
                    taskLane={item.taskLane}
                    assigneeLane={item.assigneeLane}
                    assigneeName={item.assigneeName}
                  />
                </>
              )}
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Generate 1:1 Brief button for one-on-one meetings */}
            {isOneOnOne && onGenerateBrief && (
              <button 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl transition-colors mr-2"
                style={{ 
                  backgroundColor: `${DESIGN.colors.primary}10`,
                  color: DESIGN.colors.primary
                }}
                title="Generate 1:1 Brief"
                onClick={(e) => {
                  e.stopPropagation();
                  onGenerateBrief(item.participants[0]);
                }}
                data-testid={`generate-brief-${item.id}`}
              >
                <FileText className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">1:1 Brief</span>
              </button>
            )}
            {(item.type === 'call' || item.type === 'meeting') && (
              <button 
                className="p-2.5 rounded-xl hover:bg-green-50 transition-colors"
                title="Start Call"
                onClick={() => onAction('start_call', item)}
              >
                <Phone className="w-4 h-4" style={{ color: DESIGN.colors.success }} />
              </button>
            )}
            <button 
              className="p-2.5 rounded-xl hover:bg-amber-50 transition-colors"
              title="Flag Item"
              onClick={() => onAction('flag', item)}
            >
              <Flag className="w-4 h-4" style={{ color: DESIGN.colors.amber }} />
            </button>
            <button 
              className="p-2.5 rounded-xl hover:bg-gray-100 transition-colors"
              title="More Options"
            >
              <MoreHorizontal className="w-4 h-4" style={{ color: DESIGN.colors.textMuted }} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ================================================
// TIME BLOCK HEADER - Section divider
// ================================================
export const TimeBlockHeader = ({ label, icon: Icon, accentColor }) => {
  const color = accentColor || DESIGN.colors.primary;
  
  return (
    <div className="flex items-center gap-3 mb-5 mt-8 first:mt-0">
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ 
          background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
          boxShadow: `0 0 15px ${color}15`
        }}
      >
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <h3 
        className="text-sm font-bold uppercase tracking-wider"
        style={{ color: DESIGN.colors.textSecondary }}
      >
        {label}
      </h3>
      <div className="flex-1 h-px" style={{ backgroundColor: DESIGN.colors.border }} />
    </div>
  );
};

// ================================================
// DAILY SUMMARY CARD - Today's overview
// ================================================
export const DailySummary = ({ summary, focusItems }) => {
  const dynamicStats = useMemo(() => {
    if (!focusItems || focusItems.length === 0) {
      return summary;
    }
    
    const total = focusItems.length;
    const completed = focusItems.filter(i => i.completed).length;
    const highPriority = focusItems.filter(i => 
      (i.priority === 'high' || i.priority === 'critical') && !i.completed
    ).length;
    const meetings = focusItems.filter(i => 
      i.type === 'call' || i.type === 'meeting'
    ).length;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    const completionScore = Math.round((completed / total) * 60);
    const completedHighPriority = focusItems.filter(i => 
      (i.priority === 'high' || i.priority === 'critical') && i.completed
    ).length;
    const totalHighPriority = focusItems.filter(i => 
      i.priority === 'high' || i.priority === 'critical'
    ).length;
    const priorityScore = totalHighPriority > 0 
      ? Math.round((completedHighPriority / totalHighPriority) * 25) 
      : 25;
    const engagementScore = total > 0 ? 15 : 0;
    
    const focusScore = Math.min(100, completionScore + priorityScore + engagementScore);
    
    let cogLevel = 'optimal';
    if (highPriority > 3) {
      cogLevel = 'high';
    } else if (highPriority > 1) {
      cogLevel = 'elevated';
    }
    
    return {
      ...summary,
      total_items: total,
      completed_items: completed,
      high_priority_remaining: highPriority,
      meetings_count: meetings,
      progress_percent: progress,
      focus_score: focusScore,
      cognitive_load_level: cogLevel
    };
  }, [summary, focusItems]);

  const loadConfigs = {
    optimal: { bg: DESIGN.colors.successSubtle, text: DESIGN.colors.success },
    elevated: { bg: DESIGN.colors.amberSubtle, text: '#D97706' },
    high: { bg: DESIGN.colors.errorSubtle, text: DESIGN.colors.error }
  };
  
  const loadStyle = loadConfigs[dynamicStats.cognitive_load_level] || loadConfigs.optimal;
  
  const getFocusScoreColor = (score) => {
    if (score >= 70) return DESIGN.colors.success;
    if (score >= 40) return '#D97706';
    return DESIGN.colors.error;
  };

  return (
    <div 
      className="rounded-2xl p-6 transition-all hover:shadow-lg"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        boxShadow: DESIGN.shadows.lg
      }}
      data-testid="daily-summary"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>Today's Focus</h2>
          <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>{dynamicStats.date}</p>
        </div>
        <div className="text-right">
          <p 
            className="text-3xl font-black"
            style={{ color: DESIGN.colors.primary }}
          >
            {dynamicStats.completed_items}/{dynamicStats.total_items}
          </p>
          <p className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>completed</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-5">
        <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
          <div 
            className="h-full rounded-full transition-all duration-700"
            style={{ 
              width: `${dynamicStats.progress_percent}%`,
              background: `linear-gradient(90deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryLight} 100%)`,
              boxShadow: `0 0 15px ${DESIGN.colors.primary}40`
            }}
          />
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { value: dynamicStats.high_priority_remaining, label: 'High Priority', bg: DESIGN.colors.errorSubtle, color: DESIGN.colors.error },
          { value: dynamicStats.meetings_count, label: 'Meetings', bg: DESIGN.colors.purpleGlow, color: DESIGN.colors.purple },
          { value: dynamicStats.cognitive_load_level, label: 'Load Level', bg: loadStyle.bg, color: loadStyle.text, capitalize: true }
        ].map((stat, idx) => (
          <div 
            key={idx}
            className="text-center p-4 rounded-xl"
            style={{ backgroundColor: stat.bg }}
          >
            <p 
              className={`text-xl font-black ${stat.capitalize ? 'capitalize' : ''}`}
              style={{ color: stat.color }}
            >
              {stat.value}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-wider mt-1" style={{ color: stat.color }}>
              {stat.label}
            </p>
          </div>
        ))}
      </div>
      
      {/* Focus Score */}
      <div 
        className="mt-5 p-4 rounded-xl"
        style={{ backgroundColor: DESIGN.colors.background }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.textMuted }}>
            Focus Score
          </span>
          <span 
            className="text-base font-black transition-all duration-300"
            style={{ color: getFocusScoreColor(dynamicStats.focus_score) }}
          >
            {dynamicStats.focus_score}/100
          </span>
        </div>
        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
          <div 
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{ 
              width: `${dynamicStats.focus_score}%`,
              backgroundColor: getFocusScoreColor(dynamicStats.focus_score),
              boxShadow: `0 0 10px ${getFocusScoreColor(dynamicStats.focus_score)}40`
            }}
          />
        </div>
      </div>
    </div>
  );
};

export { ICON_MAP, LaneMismatchIndicator };
export default { FocusItemCard, TimeBlockHeader, DailySummary, ICON_MAP, LaneMismatchIndicator };
